/**
 * The HDR preview inside an iPhone RAW (1.182.0, #155).
 *
 * From use, after the HEIC: "ein RAW vom iPhone". An Apple ProRAW DNG
 * (`medien/iphone-proraw.dng`, iPhone 16 Pro, 4032 x 3024) is a TIFF with
 * three pictures, measured once with a separate parser
 * (`scratchpad/tools/tiffwalk.py` of the round):
 *
 * - IFD 0, `NewSubFileType` 1 (a reduced picture): a JPEG of the full size,
 *   Apple's own rendering of the raw data. **It is an HDR JPEG of its own**:
 *   Display P3, MPF, the ISO 21496-1 segment and, as a second JPEG, the gain
 *   map at half the size (2016 x 1512, 2.85 stops), the same build as
 *   Lightroom's Ultra HDR export, which `lib/gainMap.ts` already carries.
 * - SubIFD 0, `NewSubFileType` 0: the raw data, linear, 10 bit, JPEG XL tiles.
 * - SubIFD 1: Apple's sky mask, one channel.
 *
 * So the RAW needs no rule of its own either: the photo is drawn from the
 * preview, and with `hdr_photo` the gain map goes along as for any HDR JPEG.
 * Without the switch it is the base picture alone (1.182.1, from use: "Ja"
 * to the question whether the RAW should then be Apple's rendering too): SDR
 * in Display P3, like a HEIC without the switch. What the browser would draw
 * from the DNG itself is its own rendering of the raw data, in sRGB because
 * the DNG carries no profile the canvas reads, and Chromium does not open a
 * DNG at all.
 *
 * **Only a preview as large as the photo becomes** is taken: a DNG may carry
 * a small one, and the edge of the photo must not drop for the map.
 */
import { tiffEntries } from './exif'

const DNG_VERSION = 50706
const NEW_SUBFILE_TYPE = 254
const WIDTH = 256
const HEIGHT = 257
const COMPRESSION = 259
const STRIP_OFFSETS = 273
const STRIP_BYTES = 279
const SUB_IFDS = 330
/** Old-style JPEG and JPEG (TIFF 6, 6 and 7). */
const JPEG_COMPRESSIONS = new Set([6, 7])

export interface DngPreview {
  /** The JPEG of the preview, as the DNG carries it. */
  jpeg: Uint8Array
  width: number
  height: number
  /** The longest edge of the raw data, or null if the DNG does not say. */
  rawEdge: number | null
}

/** The first value of an entry, SHORT or LONG. */
function first(view: DataView, e: { type: number; count: number; at: number } | undefined, little: boolean): number | null {
  if (e === undefined || e.count < 1) return null
  if (e.type === 3) return view.getUint16(e.at, little)
  if (e.type === 4) return view.getUint32(e.at, little)
  return null
}

function all(view: DataView, e: { type: number; count: number; at: number } | undefined, little: boolean): number[] {
  if (e === undefined || e.type !== 4) return []
  return Array.from({ length: e.count }, (_, k) => view.getUint32(e.at + 4 * k, little))
}

/**
 * The largest JPEG preview of a DNG and the edge of its raw data, or null:
 * no DNG, or no preview that is one JPEG in one strip.
 */
export function dngPreview(bytes: Uint8Array): DngPreview | null {
  if (bytes.length < 8) return null
  const little = bytes[0] === 0x49 && bytes[1] === 0x49
  const big = bytes[0] === 0x4d && bytes[1] === 0x4d
  if (!little && !big) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  if (view.getUint16(2, little) !== 42) return null
  const ifd0 = view.getUint32(4, little)
  const main = tiffEntries(view, ifd0, 0, little)
  if (!main.has(DNG_VERSION)) return null

  const dirs = [main, ...all(view, main.get(SUB_IFDS), little).map((at) => tiffEntries(view, at, 0, little))]
  let best: DngPreview | null = null
  let rawEdge: number | null = null
  for (const dir of dirs) {
    const kind = first(view, dir.get(NEW_SUBFILE_TYPE), little) ?? 0
    const width = first(view, dir.get(WIDTH), little)
    const height = first(view, dir.get(HEIGHT), little)
    if (width === null || height === null) continue
    if (kind === 0) {
      rawEdge = Math.max(rawEdge ?? 0, width, height)
      continue
    }
    // Bit 0 is "reduced resolution"; masks (bit 2) and other kinds are not previews.
    if (kind !== 1) continue
    const compression = first(view, dir.get(COMPRESSION), little)
    if (compression === null || !JPEG_COMPRESSIONS.has(compression)) continue
    const offsets = all(view, dir.get(STRIP_OFFSETS), little)
    const lengths = dir.get(STRIP_BYTES)
    const length = first(view, lengths, little)
    if (offsets.length !== 1 || length === null || lengths?.count !== 1) continue
    const from = offsets[0] ?? 0
    if (from + length > bytes.length || bytes[from] !== 0xff || bytes[from + 1] !== 0xd8) continue
    if (best === null || width * height > best.width * best.height) {
      best = { jpeg: bytes.subarray(from, from + length), width, height, rawEdge: null }
    }
  }
  return best === null ? null : { ...best, rawEdge }
}

/**
 * Whether the preview is large enough for the photo: as long as the edge the
 * photo gets from the raw data (`maxEdge` or less).
 */
export function previewSuffices(preview: DngPreview, maxEdge: number): boolean {
  const longest = Math.max(preview.width, preview.height)
  return longest >= Math.min(maxEdge, preview.rawEdge ?? longest)
}
